import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  CollectionFilterRequestBuilder,
  CollectionRequestBuilder,
} from '@web/common';
import { PublicBrandApiService } from '../api/public-brand-api.service';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { PublicBrandCategory } from '../models/public-brand-category';
import { PublicBrandDetails } from '../models/public-brand-details';
import { BrandSocialMedia } from '../../shared/models/brand-social-media';
import { PublicBrand } from '../models/public-brand';
import { PublicBrandCollectionFilter } from '../models/public-brand-collection-filter';
import { PublicBrandCosmetic } from '../models/public-brand-cosmetic';
import { valueOrDefault } from '@web/utils';
import { PublicBrandCosmeticsQuery } from '../models/public-brand-cosmetics-query';
import { PublicBrandCosmeticSortType } from '../types/public-brand-cosmetic-sort-type';
import { PublicBrandCosmeticBox } from '../models/public-brand-cosmetic-box';

@Injectable({
  providedIn: 'root',
})
export class PublicBrandDataSourceService {
  private readonly publicBrandApi = inject(PublicBrandApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public fetchCategories(): Observable<PublicBrandCategory[]> {
    return this.publicBrandApi
      .fetchCategoryCollection(
        this.collectionRequestBuilder.disablePagination().build(),
        true,
      )
      .pipe(
        map((response) =>
          response.items.map((item) => new PublicBrandCategory(item.name)),
        ),
      );
  }

  public fetchDetailsBySlug(slug: string): Observable<PublicBrandDetails> {
    return this.publicBrandApi.fetchDetailsBySlug(slug).pipe(
      map(
        (response) =>
          new PublicBrandDetails(
            response.id,
            response.name,
            response.description,
            response.homeUrl,
            response.illustration.alt(),
            response.illustration.url,
            response.socialMedia.map(
              (item) => new BrandSocialMedia(item.id, item.code, item.url),
            ),
          ),
      ),
    );
  }

  public fetchCosmeticsBySlug(options: {
    slug: string;
    query?: PublicBrandCosmeticsQuery;
    cacheable?: boolean;
  }): Observable<PublicBrandCosmetic[]> {
    const { cacheable, slug, query } = options;
    const request = this.collectionRequestBuilder
      .sorts((builder) => {
        switch (query?.sortType) {
          case PublicBrandCosmeticSortType.NameAscending:
            return [builder.ascendingFor('name').build()];

          case PublicBrandCosmeticSortType.NameDescending:
            return [builder.descendingFor('name').build()];

          case PublicBrandCosmeticSortType.CreatedAtAscending:
            return [builder.ascendingFor('createdAt').build()];

          case PublicBrandCosmeticSortType.CreatedAtDescending:
            return [builder.descendingFor('createdAt').build()];

          default:
            return [];
        }
      })
      .disablePagination()
      .build();

    return this.publicBrandApi
      .fetchCosmeticCollectionBySlug(
        slug,
        request,
        valueOrDefault(cacheable, true),
      )
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new PublicBrandCosmetic({
                boxes: item.boxes.map(
                  (box) =>
                    new PublicBrandCosmeticBox({
                      id: box.id,
                      name: box.name,
                      slug: box.slug,
                    }),
                ),
                id: item.id,
                name: item.name,
                slug: item.slug,
                featuredFile: multimediaFromResponse(item.featuredFile),
              }),
          ),
        ),
      );
  }

  public fetchHighlightedBrands(cacheable = true): Observable<PublicBrand[]> {
    const request = this.collectionRequestBuilder
      .disablePagination()
      .sorts((builder) => [builder.ascendingFor('createdAt').build()])
      .build();

    return this.publicBrandApi
      .fetchHighlightedBrandCollection(request, cacheable)
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new PublicBrand(
                item.id,
                item.name,
                item.slug,
                item.logo.alt(),
                item.logo.url,
              ),
          ),
        ),
      );
  }

  public fetchBrands(
    filter?: PublicBrandCollectionFilter,
    cacheable = true,
  ): Observable<PublicBrand[]> {
    const request = this.collectionRequestBuilder
      .disablePagination()
      .filters(
        (
          builder: CollectionFilterRequestBuilder<PublicBrandCollectionFilter>,
        ) =>
          builder
            .appendText((builder) =>
              builder //
                .startsWithFor('name')
                .value(filter?.name)
                .build(),
            )
            .build(),
      )
      .build();

    return this.publicBrandApi
      .fetchBrandCollection(request, cacheable)
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new PublicBrand(
                item.id,
                item.name,
                item.slug,
                item.logo.alt(),
                item.logo.url,
              ),
          ),
        ),
      );
  }
}
